import { useRequest } from 'ahooks'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { Modal, Pill, SearchBar, Sidebar } from '@intelie/ui'
import i18n from 'live/services/i18n'

import { packageManagerHttpServicesClient } from '../http/services'
import Markdown from './Markdown'
import styled, { css } from 'styled-components'
import Highlighter from 'react-highlight-words'

interface UpdatesModalProps {
    show?: boolean
    packageId: string
    openItemId: string
    onHide?(): void
}

const UpdatesModal: React.VFC<UpdatesModalProps> = ({ show, packageId, openItemId, onHide }) => {
    const [selectedPackage, setSelectedPackage] = useState(packageId)
    const [itemId, setItemId] = useState(openItemId)
    const scrollRef = useRef<HTMLDivElement>(null)
    const paneRef = useRef<HTMLDivElement>(null)
    const [isLoaded, setIsLoaded] = useState(false)

    const changePackage = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        setSelectedPackage(event.currentTarget.dataset.uuid)
    }, [])

    const allPackages = useRequest(async () => packageManagerHttpServicesClient.getPackages())
    const packageInfo = useRequest(async () => packageManagerHttpServicesClient.getPackage(selectedPackage), {
        manual: true
    })
    const data = allPackages.data
    const pkg = packageInfo.data

    const loading = allPackages.loading || packageInfo.loading
    const error = allPackages.error ?? packageInfo.error

    useEffect(() => {
        setSelectedPackage(packageId)
    }, [packageId])

    useEffect(() => {
        setItemId(openItemId)
    }, [openItemId])

    useEffect(() => {
        if (isLoaded || loading || !show) {
            return
        }

        setIsLoaded(true)

        setTimeout(() => {
            scrollRef.current?.scrollIntoView(true)
        }, 10)
    }, [isLoaded, loading, show])

    useEffect(() => {
        packageInfo.run()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPackage])

    const [filter, setFilter] = useState('')

    const [hasScrolled, setHasScrolled] = useState(false)

    const onScroll = useCallback((event: React.UIEvent) => {
        setHasScrolled(event.currentTarget.scrollTop > 0)
    }, [])

    if (!show) {
        return null
    }

    let body = null
    if (!isLoaded) {
        body = i18n('Loading...')
    } else if (error) {
        body = error.toString()
    } else if (data && pkg) {
        body = (
            <>
                {!packageId && (
                    <Sidebar
                        fullWidth="200px"
                        headerTitle={i18n('Installed packages')}
                        collapsedChildren={i18n('Installed packages')}
                    >
                        <StyledPackageList>
                            {data.map(pkg => (
                                <StyledPackageItem key={pkg.uuid} selected={pkg.uuid === selectedPackage}>
                                    <button type="button" data-uuid={pkg.uuid} onClick={changePackage}>
                                        <div>
                                            <b>{pkg.title || pkg.name || pkg.uuid}</b>
                                            <i>{pkg.version}</i>
                                        </div>
                                        <i className="fa fa-check" />
                                    </button>
                                </StyledPackageItem>
                            ))}
                        </StyledPackageList>
                    </Sidebar>
                )}
                <StyledFeaturesPane hasData={!packageInfo.loading}>
                    {packageInfo.loading ? (
                        i18n('Loading...')
                    ) : (
                        <>
                            <StyledSearchBar
                                placeholder={i18n('Search by feature name')}
                                filteredValue={filter}
                                handleFilter={setFilter}
                            />
                            <StyledPackageTitle>
                                {i18n('Package: %{title}', { title: pkg.title || pkg.name || pkg.uuid })}{' '}
                                <Pill size="lg">{pkg.version}</Pill>
                            </StyledPackageTitle>
                            <StyledFeatures ref={paneRef} hasScrolled={hasScrolled} onScroll={onScroll}>
                                {pkg.features
                                    .filter(feature => feature.enabled)
                                    .filter(feature =>
                                        (feature.label || feature.name || feature.uuid)
                                            .toLowerCase()
                                            .includes(filter.toLowerCase())
                                    )
                                    .map(feature => {
                                        const [currentNotes, ...changelog] = Object.entries(feature.changelog ?? {})
                                        const highlighted = feature.uuid === itemId

                                        return (
                                            <StyledFeature
                                                key={feature.uuid}
                                                ref={highlighted ? scrollRef : undefined}
                                                highlighted={highlighted}
                                            >
                                                <StyledFeatureTitle>
                                                    {i18n(`feature_type.${feature.type}`)}:{' '}
                                                    <Highlighter
                                                        autoEscape
                                                        searchWords={[filter]}
                                                        textToHighlight={feature.label || feature.name || feature.uuid}
                                                    />{' '}
                                                    <Pill>{feature.version}</Pill>
                                                </StyledFeatureTitle>
                                                <Markdown
                                                    headingStartLevel={4}
                                                    text={
                                                        currentNotes
                                                            ? currentNotes[1]
                                                            : i18n("This feature doesn't have a changelog.")
                                                    }
                                                />
                                                {changelog.length > 0 && <Changelog changelog={changelog} />}
                                            </StyledFeature>
                                        )
                                    })}
                            </StyledFeatures>
                        </>
                    )}
                </StyledFeaturesPane>
            </>
        )
    }

    return (
        <Modal closable show={show} size="680px" onHide={onHide}>
            <Modal.Header closeButton closeLabel={i18n('Close')} title={i18n("What's New?")} />
            <StyledModalContent hasData={isLoaded}>{body}</StyledModalContent>
        </Modal>
    )
}

export default UpdatesModal

interface ChangelogProps {
    changelog: [string, string][]
}

const Changelog: React.VFC<ChangelogProps> = ({ changelog }) => {
    const [open, setOpen] = useState(false)

    const toggleOpen = useCallback(() => {
        setOpen(open => !open)
    }, [])

    return (
        <>
            <StyledOlderVersionsButton type="button" open={open} onClick={toggleOpen}>
                {i18n(open ? 'Hide older versions' : 'View older versions')}
            </StyledOlderVersionsButton>
            <StyledOlderVersions open={open}>
                {changelog.map(([version, log]) => (
                    <>
                        <StyledOldVersion>
                            <Pill>{version}</Pill>
                        </StyledOldVersion>
                        <StyledOldVersionChangelog>
                            <Markdown headingStartLevel={4} text={log} />
                        </StyledOldVersionChangelog>
                    </>
                ))}
            </StyledOlderVersions>
        </>
    )
}

const StyledPackageList = styled.ul`
    all: unset;
`

const StyledPackageItem = styled.li<{ selected?: boolean }>`
    all: unset;
    list-style: none;

    button {
        display: flex;
        background: transparent;
        width: 100%;
        text-align: left;
        margin: 0;
        border: 0;
        border-bottom: 1px solid var(--gray-100);
        padding: 8px 20px;
        font-size: 12px;
        line-height: 16px;
        color: var(--blue-500);
        cursor: pointer;

        &:hover {
            color: var(--blue-700);
        }

        > i {
            font-size: 16px;
            line-height: 1;
            align-self: center;
        }

        > div {
            flex: 1;

            b,
            i {
                display: block;
            }

            b {
                font-family: var(--font-semibold);
            }
        }

        ${({ selected }) =>
            selected
                ? css`
                      background-color: var(--blue-200);
                      color: var(--gray-800);

                      > i {
                          color: var(--blue-500);
                      }
                  `
                : css`
                      > i {
                          display: none;
                      }
                  `}
    }
`

const StyledFeaturesPane = styled.div<{ hasData?: boolean }>`
    padding: 14px 24px 24px;
    flex: 1;
    display: flex;
    flex-direction: column;

    ${({ hasData }) =>
        !hasData &&
        css`
            display: flex;
            justify-content: center;
            align-items: center;
        `}
`

const StyledSearchBar = styled(SearchBar)`
    flex: none;
    margin: -14px -24px 0;
    border-width: 0 0 1px !important;
    border-radius: 0;
    padding-left: 16px;
`

const StyledPackageTitle = styled.h2`
    flex: none;
    font-family: var(--font-semibold);
    font-size: 18px;
    line-height: 24px;
    margin: 0;
    padding: 10px 0;
    color: var(--gray-800);
`

const StyledFeatures = styled.div<{ hasScrolled?: boolean }>`
    flex: 1;
    margin: 0 -24px -24px;
    padding: 14px 24px;
    overflow: auto;
    ${({ hasScrolled }) =>
        hasScrolled &&
        css`
            box-shadow: 0 -1px var(--gray-400);
        `}
`

const StyledFeature = styled.div<{ highlighted?: boolean }>`
    border-radius: 3px;
    background-color: var(--gray-0);
    border: 1px solid var(--gray-500);
    margin-bottom: 8px;
    scroll-margin-top: 8px;
    padding: 16px;

    ${({ highlighted }) =>
        highlighted &&
        css`
            background-color: var(--blue-200);
            border-color: var(--blue-500);
            border-width: 2px;
        `}
`

const StyledFeatureTitle = styled.h3`
    font-family: var(--font-semibold);
    font-size: 16px;
    line-height: 12px;
    margin: 0 0 14px;
    color: var(--gray-800);
`

const StyledModalContent = styled(Modal.Content)<{ hasData?: boolean }>`
    height: 440px;
    color: var(--gray-700);
    padding: 0;
    display: flex;

    ${({ hasData }) =>
        !hasData &&
        css`
            display: flex;
            align-items: center;
            justify-content: center;
        `}
`

const StyledOlderVersionsButton = styled.button<{ open?: boolean }>`
    all: unset;
    cursor: pointer;
    display: block;
    margin-top: 16px;
    width: 100%;
    font-family: var(--font-semibold);
    font-size: 12px;
    line-height: 12px;
    color: var(--blue-500);
    white-space: nowrap;
    overflow: hidden;

    ${({ open }) =>
        open &&
        css`
            &::after {
                content: '';
                border-top: 1px solid var(--gray-300);
                width: 100%;
                display: inline-block;
                margin-left: 8px;
                vertical-align: middle;
            }
        `}
`

const StyledOlderVersions = styled.dl<{ open?: boolean }>`
    display: ${({ open }) => (open ? 'grid' : 'none')};
    grid-template-columns: auto 1fr;
    grid-template-rows: auto;
    gap: 12px;
    margin: 16px 0 0;
`

const StyledOldVersion = styled.dt`
    justify-self: end;
`

const StyledOldVersionChangelog = styled.dd`
    font-family: inherit;
`
